import { useQueryClient } from "@tanstack/react-query"
import { ChevronLeft } from "lucide-react"
import * as React from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router"

import { type DetectorProfileResolved } from "@/components/domain/detector-profile"
import { Skeleton } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { useCurrentUser } from "@/features/auth"
import { bannerFor } from "@/lib/api-error-message"
import { ApiError } from "@/lib/errors"
import { qk } from "@/lib/query-keys"
import {
  canConverse,
  canSetClientResolution,
  canSubmitCorrectedLabel,
  isClient,
  isStaff,
} from "@/lib/roles"

import { useCreateReview } from "@/features/intake/api/create-review"

import { isReviewGone, REVIEW_QUEUE_PATH } from "../review-gone"
import { ReviewGoneState } from "../review-page-states"

import {
  useLiveInvalidation,
  useMarkMessagesReadMutation,
  usePostMessageMutation,
  useReviewQuery,
  useReviewVersionsQuery,
} from "../api"
import { useReviewCommentsQuery } from "../api/use-review-comments"
import type { Finding, FindingId, Message, ReviewId } from "../data/review-data"

import { useReportQuery } from "./api/use-report-query"
import { useReviewDocumentsQuery } from "./api/use-review-documents-query"
import { useSetClientResolutionMutation } from "./api/use-set-client-resolution"
import { ChecklistView } from "./checklist/checklist-view"
import { DocumentView } from "./document/document-view"
import { FindingMessagesDialog } from "./document/finding-messages-dialog"
import { ReportActions } from "./report-actions"
import { ReportMasthead } from "./report-masthead"
import type { ListSort, SeverityFilter } from "./list/list-data"
import { ListView } from "./list/list-view"
import {
  byNumber,
  withLocations,
  type Report,
  type ReportFindingItem,
  type ReportSectionItem,
} from "./report-data"
import { isReportTabId, type ReportTabId } from "./report-tab-ids"
import { ReportTabPanel, ReportTabs } from "./report-tabs"
import { PrintableLabel } from "./print/printable-label"
import { ScopeNotice } from "./scope-notice"
import { SummaryView } from "./summary/summary-view"

const EMPTY_MESSAGES: readonly Message[] = []
const EMPTY_FINDINGS: readonly ReportFindingItem[] = []
const EMPTY_SECTIONS: readonly ReportSectionItem[] = []

/**
 * The client report: what a reviewer opens with "Client report", and what a
 * client reads their review as.
 *
 * Findings come from `GET /reviews/{id}/report` in the client view — the same
 * object the PDF and CSV exports render — with every printed-label occurrence
 * joined in from `GET /reviews/{id}`. The tab lives in `?view=` so a client
 * can be sent straight to one.
 */
export function ReportOutputPage(): JSX.Element {
  const { id } = useParams<{ id: string }>()
  const reviewId = (id ?? null) as ReviewId | null

  const report = useReportQuery(reviewId, "client")
  const review = useReviewQuery(reviewId)
  const [searchParams, setSearchParams] = useSearchParams()

  const qc = useQueryClient()
  // One prefix covers the review, its comments and its report: a live frame
  // about any of them refreshes what this screen shows.
  const refresh = React.useCallback(() => {
    if (reviewId) void qc.invalidateQueries({ queryKey: qk.reviews.detail(reviewId) })
  }, [qc, reviewId])
  useLiveInvalidation(reviewId, refresh)

  const currentUser = useCurrentUser()
  const role = currentUser?.role
  // A client has no workspace to go back to; their way out is their own queue.
  const queue = isClient(role)
    ? { to: "/", label: "All reviews" }
    : { to: REVIEW_QUEUE_PATH, label: "Review queue" }
  const back = isClient(role)
    ? queue
    : { to: `/reviews/${reviewId ?? ""}`, label: "Back to review" }

  const error = report.error ?? review.error
  // A review that is not there has no workspace either: "Back to review" would
  // open the same missing id, so the only way out is the queue.
  // Same state as the workspace's, frame, words and all, so a missing review and its
  // missing report look alike; only the title says which was asked for.
  if (review.data === null || isReviewGone(error)) {
    return <ReviewGoneState title="This report isn't here" to={queue.to} label={queue.label} />
  }

  if (error) {
    return (
      <ReportShell back={back}>
        <ReportErrorState error={error} onRetry={() => void report.refetch()} />
      </ReportShell>
    )
  }

  if (!reviewId || !report.data || review.data === undefined) {
    return (
      <ReportShell back={back}>
        <ReportSkeleton />
      </ReportShell>
    )
  }

  return (
    <ReportShell
      back={back}
      actions={
        <ReportActions
          reviewId={reviewId}
          review={review.data}
          findingNumbers={new Map(report.data.findings.map((f) => [f.id, f.number]))}
          mayExportReviewerView={isStaff(role)}
          mayConverse={canConverse(role)}
          onFocusFinding={(findingId) => {
            // The checklist is where one finding is brought up on its own;
            // it reads the finding from the URL and clears it once shown.
            const params = new URLSearchParams(searchParams)
            params.set("view", "checklist")
            params.set("finding", findingId)
            setSearchParams(params, { replace: true })
          }}
        />
      }
    >
      <ReportBody
        reviewId={reviewId}
        report={report.data}
        documentUrl={review.data.fplUrl}
        printedOn={review.data.fplDate}
        detectorProfile={review.data.detectorProfile}
        reviewFindings={review.data.findings}
        regNo={review.data.epaRegNo}
        mayRespond={canSetClientResolution(role)}
        mayConverse={canConverse(role)}
        mayResubmit={canSubmitCorrectedLabel(role)}
        landsInReport={isClient(role)}
      />
    </ReportShell>
  )
}

function ReportBody({
  reviewId,
  report,
  documentUrl,
  printedOn,
  detectorProfile,
  reviewFindings,
  regNo,
  mayRespond,
  mayConverse,
  mayResubmit,
  landsInReport,
}: {
  reviewId: ReviewId
  report: Report
  documentUrl: string
  printedOn: string | null
  /** The settings the review applied, for the scope statement. */
  detectorProfile: DetectorProfileResolved | null
  reviewFindings: readonly Finding[]
  regNo: string
  mayRespond: boolean
  /** Post to and mark read the conversation. A viewer may not, so no message button. */
  mayConverse: boolean
  mayResubmit: boolean
  /** The reader opens reviews as their report rather than in the workspace. */
  landsInReport: boolean
}): JSX.Element {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get("view")
  // Summary first, as on the legacy console: the prose and its scope are what
  // the rest of the report is read against.
  const tab: ReportTabId = isReportTabId(tabParam) ? tabParam : "summary"
  const setTab = (next: ReportTabId): void => {
    const params = new URLSearchParams(searchParams)
    params.set("view", next)
    setSearchParams(params, { replace: true })
  }

  // A finding asked for from the conversation panel, shown by the checklist
  // and then taken off the URL, so asking for it again works.
  const focusParam = searchParams.get("finding")
  const clearFocusParam = React.useCallback(() => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current)
        params.delete("finding")
        return params
      },
      { replace: true },
    )
  }, [setSearchParams])

  // The list's controls live here rather than in the list, so they survive a
  // trip to another tab, as on the legacy console.
  const [listSort, setListSort] = React.useState<ListSort>("priority")
  const [listFilter, setListFilter] = React.useState<SeverityFilter>("all")
  const [showCorrected, setShowCorrected] = React.useState(false)
  // Fetched only once asked for: the verdict, counts and summary stay the
  // outstanding report's; only the list grows.
  const withCorrected = useReportQuery(reviewId, "client", {
    includeCorrected: true,
    enabled: showCorrected,
  })
  const listed = showCorrected ? withCorrected.data : report
  // The document view's conversation pop-up.
  const [threadFindingId, setThreadFindingId] = React.useState<FindingId | null>(null)

  const { data: versions } = useReviewVersionsQuery(reviewId)
  const documents = useReviewDocumentsQuery(reviewId)
  // A failed document list leaves the summary on its fallbacks rather than
  // loading forever, as the legacy console does.
  const summaryDocuments = documents.isError ? [] : documents.data
  const { data: conversation } = useReviewCommentsQuery(reviewId)
  const messages = conversation?.messages ?? EMPTY_MESSAGES

  const postMessage = usePostMessageMutation()
  const markRead = useMarkMessagesReadMutation()
  const setResolution = useSetClientResolutionMutation("client")
  const resubmit = useCreateReview()

  // The report arrives in priority order; the checklist walks the label in
  // reading order instead.
  const findings = React.useMemo(
    () => byNumber(withLocations(report.findings, reviewFindings)),
    [report.findings, reviewFindings],
  )

  const messagesByFinding = React.useMemo(() => {
    const map = new Map<FindingId, Message[]>()
    for (const m of messages) {
      if (!m.findingId) continue
      const list = map.get(m.findingId)
      if (list) list.push(m)
      else map.set(m.findingId, [m])
    }
    return map
  }, [messages])

  const messagesFor = React.useCallback(
    (findingId: FindingId) => messagesByFinding.get(findingId) ?? EMPTY_MESSAGES,
    [messagesByFinding],
  )
  const unreadFor = React.useCallback(
    (findingId: FindingId) =>
      (messagesByFinding.get(findingId) ?? []).filter((m) => m.unread).length,
    [messagesByFinding],
  )

  return (
    <>
      <ReportMasthead report={report} reviewId={reviewId} versions={versions ?? []} tab={tab} />

      <ScopeNotice notices={report.scopeNotices} />

      <section className="mt-10">
        <div className="flex justify-center">
          <ReportTabs value={tab} onChange={setTab} />
        </div>

        {tab === "checklist" && (
          <ReportTabPanel id="checklist">
            {/* The walkthrough is for the screen; paper gets the print copy. */}
            <PrintableLabel documentUrl={documentUrl} findings={findings} />
            <div className="print:hidden">
              <ChecklistView
                documentUrl={documentUrl}
                findings={findings}
                messagesFor={messagesFor}
                unreadFor={unreadFor}
                onSendMessage={
                  mayConverse
                    ? (findingId, body) => {
                        postMessage.mutate({ reviewId, body, findingId })
                      }
                    : undefined
                }
                onMarkRead={
                  mayConverse
                    ? (findingId) => {
                        markRead.mutate({ reviewId, findingId })
                      }
                    : undefined
                }
                onSetResolution={
                  mayRespond
                    ? (findingId, resolution) => {
                        setResolution.mutate({ reviewId, findingId, resolution })
                      }
                    : undefined
                }
                onResubmit={
                  mayResubmit
                    ? (file) => {
                        resubmit.mutate(
                          { fpl: file, regNr: regNo, previousReviewId: Number(reviewId) },
                          {
                            onSuccess: (created) => {
                              const next = String(created.id)
                              void navigate(
                                landsInReport ? `/reviews/${next}/output` : `/reviews/${next}`,
                              )
                            },
                          },
                        )
                      }
                    : undefined
                }
                resubmitPending={resubmit.isPending}
                resubmitted={resubmit.isSuccess}
                resubmitError={
                  resubmit.error ? bannerFor(resubmit.error, "the corrected label").title : null
                }
                focusFindingId={focusParam as FindingId | null}
                onFocusHandled={clearFocusParam}
              />
            </div>
          </ReportTabPanel>
        )}
        {tab === "summary" && (
          <ReportTabPanel id="summary">
            <SummaryView
              report={report}
              documents={summaryDocuments}
              printedOn={printedOn}
              detectorProfile={detectorProfile}
            />
          </ReportTabPanel>
        )}
        {tab === "list" && (
          <ReportTabPanel id="list">
            <ListView
              findings={listed?.findings ?? EMPTY_FINDINGS}
              sections={listed?.sections ?? EMPTY_SECTIONS}
              severityCounts={report.severityCounts}
              sort={listSort}
              onSortChange={setListSort}
              filter={listFilter}
              onFilterChange={setListFilter}
              showCorrected={showCorrected}
              onShowCorrectedChange={setShowCorrected}
              correctedLoading={showCorrected && !withCorrected.data && !withCorrected.isError}
              correctedFailed={showCorrected && !withCorrected.data && withCorrected.isError}
              messagesFor={messagesFor}
              unreadFor={unreadFor}
              onSendMessage={
                mayConverse
                  ? (findingId, body) => {
                      postMessage.mutate({ reviewId, body, findingId })
                    }
                  : undefined
              }
              onMarkRead={
                mayConverse
                  ? (findingId) => {
                      markRead.mutate({ reviewId, findingId })
                    }
                  : undefined
              }
            />
          </ReportTabPanel>
        )}
        {tab === "document" && (
          <ReportTabPanel id="document">
            {/* The callout diagram is for the screen; paper gets the print copy. */}
            <PrintableLabel documentUrl={documentUrl} findings={findings} />
            <div className="print:hidden">
              <DocumentView
                documentUrl={documentUrl}
                findings={findings}
                unreadFor={unreadFor}
                canPost={mayConverse}
                onOpenMessages={(findingId) => {
                  // Marking read is a write; a read-only reader opens the thread without it.
                  if (mayConverse) markRead.mutate({ reviewId, findingId })
                  setThreadFindingId(findingId)
                }}
              />
            </div>
            <FindingMessagesDialog
              finding={findings.find((f) => f.id === threadFindingId) ?? null}
              thread={threadFindingId ? messagesFor(threadFindingId) : EMPTY_MESSAGES}
              onSend={
                mayConverse && threadFindingId
                  ? (body) => {
                      postMessage.mutate({ reviewId, body, findingId: threadFindingId })
                    }
                  : undefined
              }
              onClose={() => {
                setThreadFindingId(null)
              }}
            />
          </ReportTabPanel>
        )}
      </section>
    </>
  )
}

/**
 * The page frame: a slim bar with the way back and the report's actions, then
 * the report on a white sheet. The bar is left off a printed copy.
 */
function ReportShell({
  back,
  actions,
  children,
}: {
  back: { to: string; label: string }
  /** Only once the report has loaded: there is nothing to export before it. */
  actions?: React.ReactNode
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className="h-full min-h-0 overflow-y-auto bg-surface print:h-auto print:overflow-visible">
      <div className="sticky top-0 z-sticky flex items-center justify-between gap-3 border-b border-border bg-surface/90 px-4 py-2 backdrop-blur print:hidden">
        <Button size="sm" variant="ghost" asChild>
          <Link to={back.to}>
            <ChevronLeft className="size-4" aria-hidden="true" />
            {back.label}
          </Link>
        </Button>
        {actions}
      </div>
      <main className="mx-auto max-w-[1200px] px-6 pb-24 pt-12">{children}</main>
    </div>
  )
}

function ReportErrorState({
  error,
  onRetry,
}: {
  error: unknown
  onRetry: () => void
}): JSX.Element {
  // 409: the findings exist but have not been released to this reader yet.
  if (error instanceof ApiError && error.isConflict) {
    return (
      <EmptyState
        reason="pending"
        title="The report isn't ready yet"
        body="The review team is still working on this review. The report opens here once they release it."
      />
    )
  }
  // A missing report never reaches here: the page answers it with the way to the queue.
  const banner = bannerFor(error, "this report")
  return (
    <EmptyState
      reason="error"
      title={banner.title || "Couldn't load this report"}
      body={
        banner.body ||
        "We reached the server but it did not send the report back. Try again in a moment."
      }
      action={
        <Button size="sm" onClick={onRetry}>
          Retry
        </Button>
      }
    />
  )
}

function ReportSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-4" aria-busy="true">
      <Skeleton className="h-9 w-40" />
      <Skeleton className="mt-6 h-8 w-56" />
      <Skeleton className="h-5 w-80" />
      <Skeleton className="mt-8 h-9 w-96 rounded-lg" />
      <div className="mt-6 flex w-full gap-6">
        <Skeleton className="h-[60vh] flex-1 rounded-lg" />
        <Skeleton className="hidden h-[60vh] w-[380px] rounded-lg lg:block" />
      </div>
      <span className="sr-only">Loading the report</span>
    </div>
  )
}
